<template>
  <div>
    <b-overlay :show="isLoading" rounded="sm">
      <b-container fluid>
        <b-row>
          <b-col cols="12" md="8">
            <h5 class="mb-3">Postage</h5>

            <b-row class="mb-3">
              <b-col v-if="multiOrderMode" sm="2">
                <b-row
                  v-for="(orderId, index) in orderIds"
                  v-bind:key="index"
                  class="mb-3"
                >
                  <b-col>
                    <b-form-input
                      :id="'orderId-' + index"
                      :ref="'orderId-' + index"
                      v-model="orderIds[index].orderId"
                      :state="orderIds[index].state"
                      placeholder="Order Number"
                      v-on:keyup.enter="addEmptyOrder"
                    ></b-form-input>
                    <b-form-invalid-feedback :id="'orderId-' + index"
                      >Order not found</b-form-invalid-feedback
                    >
                  </b-col>
                </b-row>

                <b-row class="mt-3">
                  <b-col>
                    <b-button-group size="sm">
                      <b-button variant="primary" @click="doneAdding"
                        >Done</b-button
                      >
                      <b-button variant="secondary" @click="cancelAdding"
                        >Cancel</b-button
                      >
                    </b-button-group>
                  </b-col>
                </b-row>
              </b-col>

              <b-col v-else sm="2">
                <b-form-input
                  id="orderId"
                  ref="orderId"
                  v-model="orderIds[0].orderId"
                  placeholder="Order Number"
                  v-on:keyup.enter="doSearch"
                  :state="orderIds[0].state"
                  :disabled="isGenerating"
                ></b-form-input>
                <b-form-invalid-feedback id="orderId"
                  >Order not found</b-form-invalid-feedback
                >
              </b-col>

              <b-col v-if="!useV2" sm="4">
                <b-form-select
                  v-model="packageType"
                  :options="packageOptions"
                  size="md"
                  class="w-100"
                >
                </b-form-select>
              </b-col>

              <b-col sm="2">
                <b-form-input
                  ref="weightInput"
                  v-model="weight"
                  placeholder="Weight"
                  v-on:keyup.enter="getLabel()"
                  :disabled="isGenerating"
                ></b-form-input>
              </b-col>

              <b-col sm="4">
                <b-form-checkbox
                  v-model="multiOrderMode"
                  name="check-button"
                  switch
                >
                  Multiple Orders
                </b-form-checkbox>

                <b-form-checkbox
                  v-model="useV2"
                  name="check-button"
                  switch
                  class="mt-2"
                >
                  Use Landmark
                </b-form-checkbox>
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <b-col sm="12">
                <b-alert :show="costMsg.length !== 0" variant="success"
                  class="d-flex justify-content-between align-items-center">
                  <span>{{ costMsg }}</span>
                </b-alert>
                
                <b-alert 
                  variant="primary"
                  :show="carrierMsg.length !== 0"
                  class="mt-1"
                ><h5>{{
                  carrierMsg
                }}</h5></b-alert>
                <b-alert
                  variant="success"
                  :show="successMsg.length"
                  fade
                  class="mt-3"
                  >{{ successMsg }}</b-alert
                >
                <b-alert :show="errorMsg.length" variant="danger">{{
                  errorMsg
                }}</b-alert>
                <b-alert :show="warningMsg.length" variant="warning">{{
                  warningMsg
                }}</b-alert>
              </b-col>
            </b-row>

            <b-row class="mb-3" v-if="availableRates.length">
              <b-col sm="12">
                <b-table striped
                     small
                     hover
                     :items="availableRates"
                     :fields="rateFields">
                    <template #cell(service)="data">
                        <b-link @click="generateLabel(data.item.rate_id)">{{ data.item.service_type }}</b-link>
                    </template>
                    <template #cell(cost)="data">
                        <span>${{ getTotalCost(data.item.rate_details) }}</span>
                    </template>
                     <template #cell(delivery)="data">
                        <span>{{ data.item.delivery_days }}</span>
                    </template>
            </b-table>

              </b-col>
            </b-row>
          </b-col>

          <b-col cols="12" md="4">
            <shipping-address
              v-if="order && order.customer"
              v-on:saveCustomer="saveCustomer($event)"
              :customer="order.customer"
              :key="addressKey"
            >
            </shipping-address>
          </b-col>
        </b-row>

        <b-row class="mt-4">
          <b-col>
            <b-card>
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <h5 class="mb-0">Today's Shipments</h5>
                <div class="d-flex align-items-center">
                  <b-form-select
                    v-if="shipmentsTodayUsers.length"
                    v-model="selectedShipmentsUsername"
                    :options="shipmentsTodayUsers"
                    size="sm"
                    class="mr-2"
                  />
                  <b-button
                    size="sm"
                    variant="outline-secondary"
                    @click="fetchTodayShipments"
                    :disabled="shipmentsTodayLoading"
                  >
                    Refresh
                  </b-button>
                </div>
              </div>

                  <b-table
                    small
                    striped
                    hover
                    :items="shipmentsToday"
                    :fields="shipmentsTodayFields"
                    :busy="shipmentsTodayLoading"
                  >
                    <template #cell(orderId)="row">
                      <b-link
                        :to="{
                          name: 'orders',
                          params: {
                            outsideQuery: {
                              orderId: row.item.orderId,
                              query: row.item.orderId,
                              filters: {},
                            },
                          },
                        }"
                        append
                      >
                        {{ row.item.orderId }}
                      </b-link>
                    </template>

                    <template #cell(tracking)="row">
                      <span v-if="row.item.tracking">{{ row.item.tracking }}</span>
                      <span v-else class="text-muted"><em>No tracking</em></span>
                    </template>

                    <template #cell(shippedAt)="row">
                      <span v-if="row.item.shippedAt">
                        {{ new Date(row.item.shippedAt).toLocaleString() }}
                      </span>
                      <span v-else class="text-muted"><em>Unknown</em></span>
                    </template>

                    <template #cell(actions)="row">
                      <b-button
                        size="sm"
                        class="mr-1"
                        variant="outline-info"
                        @click="openShipmentInfo(row.item)"
                      >
                        Info
                      </b-button>
                      <b-button
                        size="sm"
                        class="mr-1"
                        variant="outline-secondary"
                        :disabled="!row.item.labelUrl"
                        @click="reprintShipment(row.item)"
                      >
                        Reprint
                      </b-button>
                      <b-button
                        size="sm"
                        variant="outline-danger"
                        :disabled="!row.item.labelId || cancellingLabelId === row.item.labelId"
                        @click="cancelShipmentForRow(row.item)"
                      >
                        {{ cancellingLabelId === row.item.labelId ? "Cancelling..." : "Cancel" }}
                      </b-button>
                    </template>
                  </b-table>

                  <b-modal
                    v-model="showAddressModal"
                    title="Confirm Shipping Address"
                    size="lg"
                    scrollable
                    hide-footer
                    centered
                  >
                    <div
                      v-if="
                        order &&
                        order.customer &&
                        order.customer.address &&
                        validatedAddress
                      "
                    >
                      <p v-if="addressSimilarity !== null">
                        Similarity score: {{ addressSimilarity }}%
                      </p>
                      <p class="mb-3">
                        Please confirm which address to use for this international
                        shipment.
                      </p>
                      <b-row>
                        <b-col sm="6">
                          <shipping-address
                            v-if="order && order.customer"
                            :customer="order.customer"
                            :key="addressKey"
                            label="Original Address"
                            :showTaxId="false"
                            @saveCustomer="saveCustomer($event)"
                          />
                        </b-col>
                        <b-col sm="6">
                          <h5 class="mb-2">Suggested Address</h5>
                          <div class="shipping-address-form">
                            <b-form-group :disabled="true" class="mb-0">
                              <b-form-group
                                label="Ship To"
                                label-for="suggested-fullName"
                                label-cols-sm="3"
                                class="mb-1"
                              >
                                <b-form-input
                                  id="suggested-fullName"
                                  size="sm"
                                  :value="suggestedDisplayAddress.name"
                                  disabled
                                />
                              </b-form-group>
                              <b-form-group
                                label="Address 1"
                                label-for="suggested-address1"
                                label-cols-sm="3"
                                class="mb-1"
                              >
                                <b-form-input
                                  id="suggested-address1"
                                  size="sm"
                                  :value="suggestedDisplayAddress.line1"
                                  disabled
                                />
                              </b-form-group>
                              <b-form-group
                                label="Address 2"
                                label-for="suggested-address2"
                                label-cols-sm="3"
                                class="mb-1"
                              >
                                <b-form-input
                                  id="suggested-address2"
                                  size="sm"
                                  :value="suggestedDisplayAddress.line2"
                                  disabled
                                />
                              </b-form-group>
                              <!-- Tax ID intentionally omitted in this modal -->
                              <b-row class="mb-1">
                                <b-col sm="5">
                                  <b-form-group
                                    label="City"
                                    label-for="suggested-city"
                                    label-cols-sm="12"
                                    class="mb-0"
                                  >
                                    <b-form-input
                                      id="suggested-city"
                                      size="sm"
                                      :value="suggestedDisplayAddress.city"
                                      disabled
                                    />
                                  </b-form-group>
                                </b-col>
                                <b-col sm="4">
                                  <b-form-group
                                    label="State"
                                    label-for="suggested-state"
                                    label-cols-sm="12"
                                    class="mb-0"
                                  >
                                    <b-form-input
                                      id="suggested-state"
                                      size="sm"
                                      :value="suggestedDisplayAddress.state"
                                      disabled
                                    />
                                  </b-form-group>
                                </b-col>
                                <b-col sm="3">
                                  <b-form-group
                                    label="Zip"
                                    label-for="suggested-zip"
                                    label-cols-sm="12"
                                    class="mb-0"
                                  >
                                    <b-form-input
                                      id="suggested-zip"
                                      size="sm"
                                      :value="suggestedDisplayAddress.zip"
                                      disabled
                                    />
                                  </b-form-group>
                                </b-col>
                              </b-row>
                              <b-form-group
                                label="Country"
                                label-for="suggested-country"
                                label-cols-sm="3"
                                class="mb-0"
                              >
                                <b-form-select
                                  id="suggested-country"
                                  size="sm"
                                  :options="[{ value: suggestedDisplayAddress.country, text: suggestedCountryName }]"
                                  :value="suggestedDisplayAddress.country"
                                  disabled
                                />
                              </b-form-group>
                            </b-form-group>
                          </div>
                        </b-col>
                      </b-row>
                      <div class="mt-3 d-flex justify-content-end">
                        <b-button
                          size="sm"
                          variant="secondary"
                          class="mr-2"
                          @click="onChooseOriginalAddress"
                        >
                          Use Original Address
                        </b-button>
                        <b-button
                          size="sm"
                          variant="primary"
                          @click="onChooseResolvedAddress"
                        >
                          Use Suggested Address
                        </b-button>
                      </div>
                    </div>
                  </b-modal>

                  <b-modal
                    v-model="showShipmentModal"
                    title="Shipment Info"
                    size="lg"
                    scrollable
                    button-size="sm"
                    hide-footer
                    centered
                  >
                    <b-row v-if="shipmentInfo && labelInfo">
                      <b-col>
                        <h5>Shipment</h5>
                        <p>Created: {{ shipmentInfo.created_at }}</p>
                        <p>Ship Date: {{ labelInfo.ship_date }}</p>

                        <p>Service Code: {{ readableCode(shipmentInfo.service_code) }}</p>
                        <p>Shipment ID: {{ shipmentInfo.shipment_id }}</p>

                        <p>Carrier Code: {{ readableCode(labelInfo.carrier_code) }}</p>
                        <p>
                          Customs Form:
                          <a
                            v-if="labelInfo.form_download"
                            :href="labelInfo.form_download.href"
                            target="_blank"
                          >
                            {{ labelInfo.form_download.href }}
                          </a>
                        </p>
                        <p>
                          Label:
                          <a
                            v-if="labelInfo.label_download"
                            :href="labelInfo.label_download.pdf"
                            target="_blank"
                          >
                            {{ labelInfo.label_download.pdf }}
                          </a>
                        </p>

                        <p>Label ID: {{ labelInfo.label_id }}</p>
                        <p>
                          Tracking URL:
                          <a
                            v-if="labelInfo.tracking_url"
                            target="_blank"
                            :href="labelInfo.tracking_url"
                          >
                            {{ labelInfo.tracking_url }}
                          </a>
                        </p>

                        <div v-if="labelInfo.shipment_cost">
                          <p>Shipment Cost: ${{ labelInfo.shipment_cost.amount }}</p>
                        </div>

                        <h5 v-if="labelInfo.rate_details">Rate Details</h5>
                        <div
                          v-for="(rate, i) in labelInfo.rate_details"
                          :key="'rate' + i"
                        >
                          <p>Carrier Description: {{ rate.carrier_description }}</p>
                          <p v-if="rate.carrier_memo">
                            Carrier Memo: {{ rate.carrier_memo }}
                          </p>
                          <p v-if="rate.billing_source">
                            Billing Source: {{ rate.billing_source }}
                          </p>
                          <p v-if="rate.carrier_billing_code">
                            Carrier Billing Code: {{ rate.carrier_billing_code }}
                          </p>
                          <p>Amount: ${{ rate.amount.amount }}</p>
                        </div>

                        <div v-if="shipmentInfo.ship_to">
                          <h5>Ship To</h5>
                          <p>Name: {{ shipmentInfo.ship_to.name }}</p>
                          <p>Phone: {{ shipmentInfo.ship_to.phone }}</p>
                          <p>Address: {{ shipmentInfo.ship_to.address_line1 }}</p>
                          <p v-if="shipmentInfo.ship_to.address_line2">
                            Address 2: {{ shipmentInfo.ship_to.address_line2 }}
                          </p>
                          <p v-if="shipmentInfo.ship_to.address_line3">
                            Address 3: {{ shipmentInfo.ship_to.address_line3 }}
                          </p>

                          <p>City: {{ shipmentInfo.ship_to.city_locality }}</p>
                          <p>State: {{ shipmentInfo.ship_to.state_province }}</p>
                          <p>Zip: {{ shipmentInfo.ship_to.postal_code }}</p>
                          <p>Country: {{ shipmentInfo.ship_to.country_code }}</p>
                          <p>Email: {{ shipmentInfo.ship_to.email }}</p>
                        </div>

                        <div v-if="shipmentInfo.packages && shipmentInfo.packages[0]">
                          <h5>Package</h5>
                          <p>
                            Weight:
                            {{ shipmentInfo.packages[0].weight.value }}
                            {{ shipmentInfo.packages[0].weight.unit }}s
                          </p>
                          <p>
                            Dimensions:
                            {{ shipmentInfo.packages[0].dimensions.width }}" x
                            {{ shipmentInfo.packages[0].dimensions.length }}" x
                            {{ shipmentInfo.packages[0].dimensions.height }}"
                          </p>
                          <p>
                            Package Code:
                            {{ readableCode(shipmentInfo.packages[0].package_code) }}
                          </p>

                          <h5>Customs Items</h5>
                          <div
                            v-for="(product, i) in shipmentInfo.packages[0].products"
                            :key="'product' + i"
                          >
                            <p>SKU {{ product.sku }}</p>
                            <p>Value: ${{ product.value.amount }}</p>
                            <p>
                              Harmonized Tariff Code:
                              {{ product.harmonized_tariff_code }}
                            </p>
                            <p>
                              Weight:
                              {{ product.weight.value }} {{ product.weight.unit }}s
                            </p>
                          </div>
                        </div>
                      </b-col>
                    </b-row>

                    <div v-else-if="shipmentInfoLoading">
                      <p>Loading shipment info...</p>
                    </div>
                  </b-modal>
                </b-card>
          </b-col>
        </b-row>
          <!--<b-col>-->
          <!--<h5>Log</h5>-->
          <!--</b-col>-->
        <div id="foreignLabelContainer">
          <img
            id="foreignLabelImage"
            :src="label"
            @load="printForeignLabel()"
          />
        </div>
        <div id="reprintLabelContainer" style="position:absolute;left:-9999px;top:0;">
          <img id="reprintLabelImage" ref="reprintLabelImage" />
        </div>
      </b-container>
    </b-overlay>
  </div>
</template>

<script>
import ordersApi from "@/api/orders";
import shippingApi from "@/api/shipping";
import printJS from "print-js";
import ShippingAddress from "../components/shipping/ShippingAddress";
import PackageOptionsMixin from "../assets/packageOptions";
import countryList from "../assets/countryList";

export default {
  name: "Shipping",
  mixins: [ordersApi, shippingApi, PackageOptionsMixin, countryList],
  components: { ShippingAddress },
  data() {
    return {
      orderId: "",
      orderIds: [{ orderId: null, state: null }],
      weight: "",
      order: {},
      label: "",
      successMsg: "",
      errorMsg: "",
      warningMsg: "",
      costMsg: "",
      carrierMsg: "",
      isLoading: false,
      isGenerating: false,
      multiOrderMode: false,
      useV2: false,
      packageType: "",
      addressKey: 0,
      rateId: "",
      labelId: "",
      shipmentId: "",
      trackingUrl: "",
      availableRates: [],
      rateFields: [
        { key: "service", label: "Service" },
        { key: "cost", label: "Cost" },
        { key: "delivery", label: "Delivery Days" },
      ],
      shipmentsToday: [],
      shipmentsTodayAll: [],
      shipmentsTodayUsers: [],
      selectedShipmentsUsername: null,
      shipmentsTodayLoading: false,
      cancellingLabelId: null,
      selectedShipment: null,
      showShipmentModal: false,
      shipmentInfo: {},
      labelInfo: {},
      shipmentInfoLoading: false,
      shipmentsTodayFields: [
        { key: "orderId", label: "Order" },
        { key: "platformSite", label: "Platform" },
        { key: "customerName", label: "Customer" },
        { key: "shippingMethod", label: "Method" },
        { key: "serviceCode", label: "Service Code" },
        { key: "packageCode", label: "Package Code" },
        { key: "tracking", label: "Tracking #" },
        { key: "shippedAt", label: "Shipped At" },
        { key: "actions", label: "" },
      ],
      validatedAddress: null,
      addressSimilarity: null,
      showAddressModal: false,
      skipInternationalValidationOnce: false,
      addressSimilarityThreshold: 80,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.orderId.$el.focus();
    });
    this.fetchTodayShipments();
  },
  methods: {
    buildFlagMessage(thisOrder) {
      let hasFlags = false;
      let flagMsg = "";

      if (
        (thisOrder.flags && Object.keys(thisOrder.flags).length > 0) ||
        !thisOrder.payment ||
        thisOrder.payment.length === 0 ||
        !thisOrder.platform.site
      ) {
        if (thisOrder.flags) {
          for (let flag in thisOrder.flags) {
            if (thisOrder.flags[flag]) {
              if (flag === "unmerged") {
                flagMsg +=
                  "There are other unshipped orders for this customer.\n";
                hasFlags = true;
              } else if (flag === "noPhone" && !thisOrder.customer.phone) {
                hasFlags = true;
                flagMsg +=
                  "The customer has not provided a phone number.\n";
              } else if (
                flag === "noLatinTaxId" &&
                !thisOrder.customer.address.line3
              ) {
                hasFlags = true;
                flagMsg +=
                  "The customer has not provided a Latin tax ID.\n";
              } else if (flag === "other") {
                hasFlags = true;
                flagMsg += `Flag set: ${thisOrder.flags[flag]}\n`;
              } else if (flag === "zeroQtyItems") {
                hasFlags = true;
                flagMsg += "The order has 0 quantity items.\n";
              }
            }
          }
        }

        if (!thisOrder.payment || thisOrder.payment.length === 0) {
          hasFlags = true;
          flagMsg += "The order has no payment.\n";
        }

        if (thisOrder.location && thisOrder.location.includes("Cancelled")) {
          hasFlags = true;
          flagMsg += "The order is cancelled.\n";
        }

        if (thisOrder.location && thisOrder.location.includes("Refunded")) {
          hasFlags = true;
          flagMsg += "The order is refunded.\n";
        }

        if (!thisOrder.platform.site) {
          hasFlags = true;
          flagMsg += "The order has no platform.\n";
        }
      }

      return { hasFlags, flagMsg };
    },
    readableCode(snakeStr) {
      if (!snakeStr) {
        return "";
      }
      return snakeStr
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    getTotalCost(rateDetails) {
      let total = 0;
      rateDetails.forEach((rate) => {
        total += rate.amount.amount;
      });
      console.log(`total`, total);
      return total.toFixed(2);
    },
    async saveCustomer(customer) {
      try {
        await this.updateOrderCustomer(this.orderIds[0].orderId, {
          customer: customer,
        });
        if (this.order && typeof this.order === "object") {
          this.order = {
            ...this.order,
            customer: customer,
          };
          this.addressKey += 1;
        }
      } catch (err) {
        this.order = await this.getOrder(this.orderIds[0].orderId);
        this.addressKey += 1;
        this.errorMsg = `Error updating customer: ${err.response.data.message}`;
      }
    },
    async generateLabel(rateId) {
        this.rateId = rateId;
        this.getLabel();
    },
    async cancelShipment() {
      console.log("cancelShipment");
      console.log(this.labelId);
    },
    async getLabel() {
      const orderIdPayload = this.orderIds.map((o) => o.orderId);
      this.isGenerating = true;
      console.log(`this.skipInternationalValidationOnce`, this.skipInternationalValidationOnce);
      if (!this.useV2) {
        if (this.rateId.length > 0) {
          this.warningMsg = "Generating postage label...";
        } else if (!this.isDomestic) {
          this.warningMsg = this.skipInternationalValidationOnce
            ? "Getting rates..."
            : "Validating address...";
        } else {
          this.warningMsg = "Generating postage label...";
        }
      } else {
        this.warningMsg = "Generating postage label...";
      }
      this.costMsg = "";

      try {
        if (this.isDomestic) {
          const label = await this.getDomesticLabel(
            orderIdPayload,
            this.weight,
            this.packageType
          );
          this.label = label.label;
          if (label && label.labelData && label.labelData.shipment_cost) {
            this.labelId = label.labelData?.label_id;
            console.log(`labelId`, this.labelId);
            this.costMsg = `Last shipment cost: $${label.labelData?.shipment_cost?.amount}`;
            if (label.labelData?.tracking_url && label.labelData?.tracking_url.includes("fedex")) {
              this.carrierMsg = `FEDEX`;
            }
          }
          printJS(this.label, "image");

          this.resetForm();
        } else {
          // International
          if (!this.skipInternationalValidationOnce && !this.useV2) {
            // Validate address with FedEx before getting rates or labels
            try {
              console.log(`validating address`);
              const validation = await this.validateFedexAddress(orderIdPayload);
              console.log(`validation`, validation);
              this.validatedAddress = validation.resolvedAddress || null;
              this.addressSimilarity = validation.similarityScore || null;
            } catch (validationError) {
              this.isLoading = false;
              if (
                validationError.response &&
                validationError.response.data &&
                validationError.response.data.message
              ) {
                this.errorMsg = validationError.response.data.message;
              } else {
                this.errorMsg = "Unable to validate international address.";
              }
              this.warningMsg = "";
              this.isGenerating = false;
              return;
            }

            const threshold = this.addressSimilarityThreshold;
            if (
              this.validatedAddress &&
              typeof this.addressSimilarity === "number" &&
              this.addressSimilarity <= threshold
            ) {
              this.showAddressModal = true;
              this.warningMsg = "";
              this.isGenerating = false;
              return;
            }
          }

          // At this point the international address has been validated
          // and either (a) passed the threshold without needing a modal
          // or (b) a modal was already accepted and we skipped validation.
          // If we're about to fetch rates (no rateId yet), reflect that in the UI.
          if (!this.useV2 && !this.isDomestic && !this.rateId.length) {
            this.warningMsg = "Getting rates...";
          }

          if (!this.useV2) {
            if (this.rateId.length > 0) {
              const label = await this.getForeignLabelFedEx(
                orderIdPayload,
                this.weight,
                this.packageType,
                this.rateId,
                this.validatedAddress
              );
              console.log(`label`, label);
              this.label = label.labelData?.label_download?.png;
              this.costMsg = `Last shipment cost: $${label.labelData?.shipment_cost?.amount}`;
              if (label.labelData?.tracking_url && label.labelData?.tracking_url.includes("fedex")) {
                this.carrierMsg = `FEDEX`;
              }
              this.availableRates = [];
              this.rateId = "";
              this.labelId = label.labelData?.label_id;
              this.shipmentId = label.labelData?.shipment_id;
              this.trackingUrl = label.labelData?.tracking_url;
              if (this.multiOrderMode) {
                this.multiOrderMode = false;
              }
            } else {
              // get rates to choose from
              const rates = await this.getForeignLabelFedEx(
                orderIdPayload,
                this.weight,
                this.packageType,
                null,
                this.validatedAddress
              );
              console.log(`rates`, rates);
              this.availableRates = [...rates.labelData.rates];
              if (rates.validationError && rates.validationError.length > 0) {
                this.errorMsg = rates.validationError;
              }
            }
          } else {
            const label = await this.getForeignLabel(
              orderIdPayload,
              this.weight
            );
            console.log(`label`, label);
            this.label = label.label;
            if (this.multiOrderMode) {
              this.multiOrderMode = false;
            }
          }
        }

      
      } catch (error) {
        this.isLoading = false;
        if (
          error.response &&
          error.response.status &&
          error.response.status === 404
        ) {
          this.errorMsg = `Label creation service is not responding.`;
        } else {
          if (
            error.response &&
            error.response.data &&
            error.response.data.message
          ) {
            this.errorMsg = error.response.data.message;
          } else {
            this.errorMsg = `An unknown error happened.`;
            console.log(error);
          }
        }
      }
      this.warningMsg = "";
      this.isGenerating = false;
    },
    async onChooseResolvedAddress() {
      const addr =
        this.order &&
        this.order.customer &&
        this.order.customer.address
          ? this.order.customer.address
          : null;
      const validatedAddress = this.validatedAddress;
      
      // call the api to log that the resolved address was chosen
      await this.addressChosen(this.orderIds, validatedAddress, addr, 'Validated');

      this.showAddressModal = false;
      this.skipInternationalValidationOnce = true;
      this.isGenerating = false;
      this.warningMsg = "";
      this.getLabel();
    },
    async onChooseOriginalAddress() {
      
      const addr =
        this.order &&
        this.order.customer &&
        this.order.customer.address
          ? this.order.customer.address
          : null;

      const validatedAddress = this.validatedAddress;
      
      // call the api to log that the original address was chosen
      await this.addressChosen(this.orderIds, validatedAddress, addr, 'Customer');

      if (addr) {
        this.validatedAddress = {
          streetLinesToken: [addr.line1, addr.line2].filter(Boolean),
          city: addr.city,
          stateOrProvinceCode: addr.state,
          postalCode: addr.zip,
          countryCode: addr.country,
        };
      } else {
        this.validatedAddress = null;
      }

      this.addressSimilarity = null;
      this.showAddressModal = false;
      this.skipInternationalValidationOnce = true;
      this.isGenerating = false;
      this.warningMsg = "";
      this.getLabel();
    },
    printForeignLabel() {
      if (this.label) {
        printJS("foreignLabelImage", "html");

        this.resetForm();
      }
    },
    resetForm() {
      this.isLoading = false;
      this.orderId = "";
      this.order = {};
      this.orderIds = [];
      this.shipmentId = "";
      this.labelId = "";
      this.trackingUrl = "";
      this.weight = "";
      this.packageType = "";
      this.validatedAddress = null;
      this.addressSimilarity = null;
      this.showAddressModal = false;
      this.skipInternationalValidationOnce = false;
      this.addEmptyOrder();

      if (!this.multiOrderMode) {
        this.$nextTick(() => {
          this.$refs.orderId.$el.focus();
        });
      } else {
        this.$nextTick(() => {
          this.$refs["orderId-0"][0].$el.focus();
        });
      }

      // Refresh today's shipments after each label/reset
      this.fetchTodayShipments();
    },
    clearErrors() {
      this.errorMsg = "";
      this.warningMsg = "";
    },
    resetShipmentStateForNewOrder() {
      this.clearErrors();
      this.label = "";
      this.costMsg = "";
      this.carrierMsg = "";
      this.shipmentId = "";
      this.labelId = "";
      this.trackingUrl = "";
      this.weight = "";
      this.packageType = "";
      this.availableRates = [];
      this.rateId = "";
      this.validatedAddress = null;
      this.addressSimilarity = null;
      this.showAddressModal = false;
      this.skipInternationalValidationOnce = false;
    },
    async doSearch() {
      if (this.orderIds.length) {
        try {
          this.resetShipmentStateForNewOrder();

          this.isLoading = true;

          let thisOrder = await this.getOrder(this.orderIds[0].orderId);
          console.log("thisOrder", thisOrder);
          const { hasFlags, flagMsg } = this.buildFlagMessage(thisOrder);

          if (hasFlags) {
            const currentOrderId = this.orderIds[0].orderId;

            if (this.orderIds[0]) {
              this.orderIds[0].orderId = null;
              this.orderIds[0].state = null;
            }

            this.errorMsg = `Order ${currentOrderId}: ${flagMsg}`;
            this.isLoading = false;
            return;
          }

          this.order = thisOrder;
          this.addressKey += 1;
          this.isLoading = false;

          if (typeof this.order !== "object") {
            this.orderIds[0].state = false;
          } else {
            // if (this.isDomestic) {
            this.$nextTick(() => {
              this.$refs.weightInput.$el.focus();
            });
            // } else {
            // this.getLabel();
            // }
          }
        } catch (error) {
          if (!error.response) {
            this.errorMsg = "Unable to load the order. Try again later.";
          } else if (error.response.status === 404) {
            this.errorMsg = "Invalid order number.";
          } else {
            this.errorMsg = error.message;
          }

          this.isLoading = false;
        }
      }
    },
    addEmptyOrder() {
      this.orderIds.push({ orderId: null, state: null });

      this.$nextTick(() => {
        if (
          this.$refs["orderId-" + (this.orderIds.length - 1)] &&
          this.$refs["orderId-" + (this.orderIds.length - 1)][0]
        ) {
          this.$refs["orderId-" + (this.orderIds.length - 1)][0].$el.focus();
        }
      });
    },
    async doneAdding() {
      for (let i = 0; i < this.orderIds.length; i++) {
        if (!this.orderIds[i].orderId) {
          this.orderIds.splice(i, 1);
        }
      }

      let orders = [];
      let hasErrors = false;

      // verify that all these orders can be found
      for (let i = 0; i < this.orderIds.length; i++) {
        const orderId = this.orderIds[i].orderId;
        try {
          const order = await this.getOrder(orderId);

          if (order) {
            const { hasFlags, flagMsg } = this.buildFlagMessage(order);
            if (hasFlags) {
              this.orderIds[i].state = false;
              hasErrors = true;
              this.errorMsg = `Order ${orderId}: ${flagMsg}`;
            } else {
              orders.push(order);
            }
          } else {
            this.orderIds[i].state = false;
            hasErrors = true;
          }
        } catch (err) {
          this.orderIds[i].state = false;
          hasErrors = true;
        }
      }

      if (!hasErrors) {
        this.order = orders[0];
        this.resetShipmentStateForNewOrder();
        this.addressKey += 1;

        // if (this.isDomestic) {
        this.$nextTick(() => {
          this.$refs.weightInput.$el.focus();
        });
        // } else {
        // this.getLabel();
        // }
      }
    },
    cancelAdding() {
      this.orderIds = [];
      this.order = {};
      this.addEmptyOrder();
    },
    async cancelShipmentForRow(item) {
      if (!item.labelId) {
        return;
      }

      // Native confirm dialog (no Bootstrap-Vue modal)
      const confirmed = window.confirm(
        `Cancel shipment ${item.tracking || ""}?`
      );
      if (!confirmed) {
        return;
      }

      this.cancellingLabelId = item.labelId;

      try {
        await this.cancelLabel(item.labelId);
        this.successMsg = "Shipment cancelled.";
        await this.fetchTodayShipments();
      } catch (err) {
        const msg =
          err &&
          err.response &&
          err.response.data &&
          err.response.data.message
            ? err.response.data.message
            : err && err.message
            ? err.message
            : "Error cancelling shipment.";

        // eslint-disable-next-line no-console
        console.error("Error cancelling shipment", err);
        this.errorMsg = msg;
      } finally {
        this.cancellingLabelId = null;
      }
    },
    reprintShipment(item) {
      if (!item.labelUrl) {
        return;
      }
      const img = document.getElementById("reprintLabelImage");
      if (!img) return;
      const onLoad = () => {
        img.removeEventListener("load", onLoad);
        printJS("reprintLabelImage", "html");
      };
      img.addEventListener("load", onLoad);
      img.src = item.labelUrl;
      if (img.complete) {
        img.removeEventListener("load", onLoad);
        printJS("reprintLabelImage", "html");
      }
    },
    async openShipmentInfo(item) {
      this.selectedShipment = item;
      this.showShipmentModal = true;
      this.shipmentInfo = {};
      this.labelInfo = {};
      this.shipmentInfoLoading = true;

      try {
        const shipmentInfo = await this.getShipmentData(item.shipmentId);
        this.shipmentInfo = shipmentInfo.shipmentData || shipmentInfo.shipment;

        const labelInfo = await this.getLabelData(item.tracking);
        this.labelInfo = labelInfo.labelData || labelInfo.label;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error loading shipment info", err);
        this.errorMsg = "Error loading shipment info.";
        this.showShipmentModal = false;
      } finally {
        this.shipmentInfoLoading = false;
      }
    },
    async fetchTodayShipments() {
      this.shipmentsTodayLoading = true;

      try {
        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

        const shipments = await this.getShipmentsForDate(today);

        // Flatten API response into one row per shipment with order info (serviceCode/packageCode from DB)
        const flattened = (shipments || []).flatMap((entry) => {
          const order = entry.order || {};
          const method =
            order.shipping && order.shipping.method
              ? order.shipping.method
              : null;

          return (entry.shipments || []).map((shipment) => ({
            orderId: order.orderId,
            platformSite: order.platform && order.platform.site,
            customerName: order.customer && order.customer.fullName,
            shippingMethod: method,
            tracking: shipment.tracking,
            shippedAt: shipment.date,
            labelId: shipment.labelId,
            shipmentId: shipment.shipmentId,
            labelCost: shipment.labelCost,
            trackingUrl: shipment.trackingUrl,
            labelUrl: shipment.labelUrl,
            weight: shipment.weight,
            username: shipment.username,
            serviceCode: shipment.serviceCode
              ? this.readableCode(shipment.serviceCode)
              : null,
            packageCode: shipment.packageCode
              ? this.readableCode(shipment.packageCode)
              : null,
          }));
        });

        // Sort by shippedAt descending (newest first)
        this.shipmentsTodayAll = flattened.sort((a, b) => {
          const ad = a.shippedAt ? new Date(a.shippedAt).getTime() : 0;
          const bd = b.shippedAt ? new Date(b.shippedAt).getTime() : 0;
          return bd - ad;
        });

        // Build username options from returned shipments
        const usernames = Array.from(
          new Set(
            this.shipmentsTodayAll
              .map((row) => row.username)
              .filter((u) => !!u)
          )
        ).sort();
        this.shipmentsTodayUsers = usernames;

        // Default selected username: logged-in user if present in list; otherwise first username (if any)
        const currentUser =
          this.$auth && this.$auth.user && this.$auth.user.nickname;
        if (!this.selectedShipmentsUsername) {
          if (currentUser && usernames.includes(currentUser)) {
            this.selectedShipmentsUsername = currentUser;
          } else {
            this.selectedShipmentsUsername = usernames[0] || null;
          }
        }

        this.applyShipmentsUserFilter();
      } catch (err) {
        if (this.showError) {
          this.showError(err, "Today's Shipments");
        } else {
          // Fallback: log error
          // eslint-disable-next-line no-console
          console.error("Error fetching today's shipments", err);
        }
      } finally {
        this.shipmentsTodayLoading = false;
      }
    },
    applyShipmentsUserFilter() {
      if (!this.selectedShipmentsUsername) {
        this.shipmentsToday = this.shipmentsTodayAll.slice();
        return;
      }
      this.shipmentsToday = this.shipmentsTodayAll.filter(
        (row) => row.username === this.selectedShipmentsUsername
      );
    },
  },
  computed: {
    isDomestic() {
      return (
        this.order &&
        this.order.customer &&
        (this.order.customer.address.country === "US" ||
          this.order.customer.address.country === "PR" ||
          (this.order.hubShipTo &&
            Object.keys(this.order.hubShipTo).length > 0))
      );
    },
    suggestedDisplayAddress() {
      const addr =
        this.order &&
        this.order.customer &&
        this.order.customer.address
          ? this.order.customer.address
          : {};

      const streetLines =
        (this.validatedAddress &&
          this.validatedAddress.streetLinesToken) ||
        [];

      return {
        name: addr.name || this.order?.customer?.fullName || "",
        line1: streetLines[0] || "",
        line2: streetLines[1] || "",
        line3: addr.line3 || "",
        city: this.validatedAddress?.city || "",
        state: this.validatedAddress?.stateOrProvinceCode || "",
        zip: this.validatedAddress?.postalCode || "",
        country: this.validatedAddress?.countryCode || "",
      };
    },
    suggestedCountryName() {
      const code = this.suggestedDisplayAddress.country;
      if (!code || !this.countries) {
        return code || "";
      }
      const match = this.countries.find((c) => c.code === code);
      return match ? match.name : code;
    },
  },
  watch: {
    selectedShipmentsUsername() {
      this.applyShipmentsUserFilter();
    },
    multiOrderMode() {
      if (this.multiOrderMode) {
        this.$nextTick(() => {
          this.$refs["orderId-0"][0].$el.focus();
        });
      } else {
        this.orderIds = [{ orderId: null, state: null }];
        this.$nextTick(() => {
          this.$refs.orderId.$el.focus();
        });
      }
    },
  },
};
</script>

<style scoped>
#foreignLabelContainer {
  position: absolute;
  left: -9999px;
}
</style>